<script setup lang="ts">
  import { ref } from 'vue'
  import { ROUTES } from '@/router/routes'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import { type TForm } from '@/types/generated/types.gen'
  import { FormsService } from '@/types/generated/services.gen'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import CustomerTableDataCell from '@/components/admin/table/CustomerTableDataCell.vue'
  import type { FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableRow, TableCell } from '@/components/ui/table'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { ExternalLink, DownloadIcon } from 'lucide-vue-next'
  import { useRouter } from 'vue-router'
  import { genericErrorToast } from '@/lib/toasts'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'

  const router = useRouter()

  const showDeleteModal = ref(false)
  const documentToDelete = ref<TForm | null>(null)
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  const fetchDocuments = async (options: FetchDataOptions) => {
    return FormsService.formsList({
      limit: options.limit,
      offset: options.offset,
      search: options.search,
      tag: options.tag
    })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const openInNewTab = (documentId: string) => {
    const url = documentId

    if (typeof window !== 'undefined' && window.open) {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      router.push(url)
    }
  }

  const downloadPDF = async (form: TForm) => {
    try {
      const response = await fetch(form.file ?? '')
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${form.title}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      genericErrorToast()
    }
  }

  const openDeleteModal = (document: TForm) => {
    documentToDelete.value = document
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    documentToDelete.value = null
  }

  const confirmDelete = async () => {
    if (documentToDelete.value) {
      try {
        await FormsService.formsDestroy({
          id: documentToDelete.value.id
        })
        closeDeleteModal()
        if (paginatedTableRef.value) {
          await paginatedTableRef.value.refreshData()
        }
      } catch (error) {
        genericErrorToast()
      }
    }
  }
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Documents', to: ROUTES.ADMIN.DOCUMENTS.DOCUMENTS_LIST }
          ]"
        />

        <div class="flex items-center justify-end gap-x-2">
          <router-link
            class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
              text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
              focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
            :to="ROUTES.ADMIN.DOCUMENTS.DOCUMENT_CREATE"
          >
            <svg
              class="hidden size-3 flex-shrink-0 sm:block"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8 1C8.55228 1 9 1.44772 9 2V7L14 7C14.5523 7 15 7.44771 15 8C15 8.55228 14.5523 9 14 9L9 9V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V9.00001L2 9.00001C1.44772 9.00001 1 8.5523 1 8.00001C0.999999 7.44773 1.44771 7.00001 2 7.00001L7 7.00001V2C7 1.44772 7.44772 1 8 1Z"
              ></path>
            </svg>
            Add PDF document
          </router-link>
        </div>
      </div>
      <div
        class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm
          dark:border-neutral-700 dark:bg-neutral-800"
      >
        <PaginatedTableView
          ref="paginatedTableRef"
          :fetchData="fetchDocuments"
          :fetch-options="{ tag: 'MASTER' }"
          searchableFields="document name, customer name"
        >
          <template #table-head>
            <TableCell>UUID</TableCell>
            <TableCell class="w-1/4">Document name</TableCell>
            <TableCell class="w-1/4">Description</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell>Created date</TableCell>
            <TableCell>Actions</TableCell>
          </template>
          <template #table-row="{ items }">
            <TableRow
              v-for="document in items"
              :key="document.uuid"
            >
              <UniqueTableDataCell :truncate="true">
                <router-link
                  :to="
                    ROUTES.ADMIN.DOCUMENTS.DOCUMENT_DETAIL.replace(':uuid', (document as TForm).id)
                  "
                >
                  {{ (document as TForm).id }}
                </router-link>
              </UniqueTableDataCell>
              <TableCell>
                <div
                  class="truncate"
                  :title="(document as TForm).title"
                >
                  {{ (document as TForm).title }}
                </div>
              </TableCell>
              <TableCell>
                <div
                  class="truncate"
                  :title="(document as TForm).description"
                >
                  {{ (document as TForm).description }}
                </div>
              </TableCell>
              <CustomerTableDataCell :org="(document as TForm).organisation" />
              <TableCell>
                {{ formatDate((document as TForm).created_at) }}
              </TableCell>
              <TableCell>
                <TableOptionsButton
                  :options="[
                    {
                      label: 'Open In New Tab',
                      icon: ExternalLink,
                      onClick: () => openInNewTab((document as TForm).file ?? '')
                    },
                    {
                      label: 'Download PDF',
                      icon: DownloadIcon,
                      onClick: () => downloadPDF(document as TForm)
                    },
                    {
                      label: 'Delete',
                      onClick: () => openDeleteModal(document as TForm)
                    }
                  ]"
                />
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>
      </div>
    </div>
    <!-- Delete Confirmation Modal -->
    <Dialog
      :open="showDeleteModal"
      @update:open="closeDeleteModal"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the document "{{ documentToDelete?.title }}"? This
            action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            @click="closeDeleteModal"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            @click="confirmDelete"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped>
  .truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
